import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Popover, Grid, Link, Typography } from '@mui/material';
import { IconButton } from '@totalsoft/rocket-ui';
import { ArrowBack, Assignment, AccountTree, List, Remove, CompareArrows, History, Cached } from '@mui/icons-material';
import FacilityStatesSelectionListContainer from '../../edit/states/components/FacilityStatesSelectionListContainer';
import { getFacilityRestartRenewalData } from '../selectors';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { restartOrRenewFacility } from '../actionCreators';
import { withParams } from 'providers/ParamsConsumer';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import { RightsContext } from 'providers/RightsContext';
import { showInfo } from 'actions/actionCreators/snackbarActions';

const emptyArray = Object.freeze([]);
const emptyObject = Object.freeze({});

const FacilityHeader = ({
  documentId,
  siteId,
  data,
  onChangeActiveStep,
  history,
  onChangeShowReportsModal,
  loadFacilityReports,
  openFacilityActions,
  handleCloseFacilityActions,
  handleOpenFacilityActions,
  params,
  anchorEl
}) => {
  const { t } = useTranslation();

  const defaultGridActions = 4;
  const defaultGridSpace = 3;
  const { offerId, offerSiteId, documentStateCode } = useSelector(state => getFacilityRestartRenewalData(documentId, state), shallowEqual);
  const { addFacility } = useContext(GlobalRightsContext);
  const { documentReportsRead, otherStateTransitionHistoryRead } = useContext(RightsContext);
  const validatedFacilityStateCodes = useMemo(() => {
    if (!params.clientFacilityConfiguration) return emptyArray;
    return JSON.parse(params.clientFacilityConfiguration)?.ValidatedFacilityStateCodes?.split(';');
  }, [params.clientFacilityConfiguration]);

  const facilityActionsConfiguration = useMemo(() => {
    if (!params.facilityActionsConfiguration) return emptyObject;
    return JSON.parse(params.facilityActionsConfiguration);
  }, [params.facilityActionsConfiguration]);

  const isRestartAvailable =
    addFacility && offerId && validatedFacilityStateCodes?.includes(documentStateCode) && facilityActionsConfiguration?.RestartFacility;
  const isRenewalAvailable =
    offerId && validatedFacilityStateCodes?.includes(documentStateCode) && facilityActionsConfiguration?.RenewFacility;

  const dispatch = useDispatch();

  const onCloseClick = useCallback(() => {
    history.push('/facilities');
  }, [history]);

  const onPropertiesClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/facilities/${documentId}/${siteId}/document-properties`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onReportsClick = useCallback(() => {
    onChangeShowReportsModal(true);
    loadFacilityReports(documentId, siteId);
  }, [documentId, loadFacilityReports, onChangeShowReportsModal, siteId]);

  const onStateTransitionsClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/facilities/${documentId}/${siteId}/state-transitions`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const redirectToClonedOffer = useCallback(
    info => () => {
      history.push(`/offers/${info.newOfferId}/${siteId}/generalInfo`);
    },
    [history, siteId]
  );

  const redirectToClonedFacility = useCallback(
    info => () => {
      history.push(`/facilities/${info.newFacilitytId}/${siteId}/generalData`);
    },
    [history, siteId]
  );

  const composeToastContent = info => {
    const newOfferNumber = info.newOfferNumber;
    const newFacilityNumber = info.newFacilityNumber;

    const createdDocumentText = t('Notifications.Charisma.Leasing.PublishedLanguage.Events.LeasingFacility.FacilityRestartedOrRenewed', {
      newOfferNumber,
      newFacilityNumber
    });

    const partsBeforeAndAfterOfferNumber = createdDocumentText.split(newOfferNumber);
    const createdDocumentLinkForOffer = (
      <Link component='button' underline='hover' onClick={redirectToClonedOffer(info)}>
        {newOfferNumber}
      </Link>
    );

    const partsBeforeAndAfterFacilityNumber = partsBeforeAndAfterOfferNumber[1].split(newFacilityNumber);
    const createdDocumentLinkForFacility = (
      <Link component='button' underline='hover' onClick={redirectToClonedFacility(info)}>
        {newFacilityNumber}
      </Link>
    );

    const toastContentWithOfferLinkAndFacilityLink = (
      <div>
        {partsBeforeAndAfterOfferNumber[0]}
        {createdDocumentLinkForOffer}
        {partsBeforeAndAfterFacilityNumber[0]}
        {createdDocumentLinkForFacility}
        {partsBeforeAndAfterFacilityNumber[1]}
      </div>
    );

    return toastContentWithOfferLinkAndFacilityLink;
  };

  const onRestartFacilityClick = async () => {
    const response = await dispatch(restartOrRenewFacility(documentId, siteId, offerId, offerSiteId));

    const toastContent = composeToastContent(response.eventBody);
    dispatch(showInfo(toastContent, false));
  };

  const onRenewFacilityClick = () => {
    dispatch(restartOrRenewFacility(documentId, siteId, offerId, offerSiteId, true));
  };

  const getNumberOfGridActions = () => {
    return defaultGridActions;
  };

  return (
    <Grid container alignItems='center' justifyContent='space-between'>
      <Grid item xs alignItems='center' display='flex' gap={2} flexShrink={1} minWidth={0}>
        <Typography fontWeight='bold' variant='subtitle1' noWrap>
          {data?.documentNumber ? t('Facility.PageTitle', { data: data }) : t('Facility.NewFacilityPageTitle')}
        </Typography>
        <FacilityStatesSelectionListContainer documentId={documentId} siteId={siteId} />
      </Grid>
      <Grid item container xs='auto' justifyContent='end'>
        <IconButton onClick={onCloseClick} title={t('Facility.Action.BackToList')} variant='text' color='primary'>
          <ArrowBack />
        </IconButton>
        <Popover
          open={openFacilityActions}
          anchorEl={anchorEl}
          onClose={handleCloseFacilityActions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Grid container spacing={2} p={1} justifyContent='center'>
            {documentReportsRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  onClick={onReportsClick}
                  id={'facilityHeader_actionList_reports'}
                  tooltip={t('Facility.Action.Reports')}
                  variant='text'
                  color='primary'
                >
                  <Assignment />
                </IconButton>
              </Grid>
            )}
            <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
              <IconButton
                onClick={onPropertiesClick}
                id={'facilityHeader_actionList_properties'}
                tooltip={t('Facility.Action.Properties')}
                variant='text'
                color='primary'
              >
                <AccountTree />
              </IconButton>
            </Grid>
            {otherStateTransitionHistoryRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  onClick={onStateTransitionsClick}
                  id={'facilityHeader_actionList_stateTransitions'}
                  tooltip={t('Facility.Action.StateTransitions')}
                  variant='text'
                  color='primary'
                >
                  <CompareArrows />
                </IconButton>
              </Grid>
            )}
            {isRestartAvailable && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  onClick={onRestartFacilityClick}
                  id={'facilityHeader_actionList_restartFacility'}
                  tooltip={t('Facility.Action.RestartFacility')}
                  variant='text'
                  color='primary'
                >
                  <History />
                </IconButton>
              </Grid>
            )}
            {isRenewalAvailable && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  onClick={onRenewFacilityClick}
                  id={'facilityHeader_actionList_renewFacility'}
                  tooltip={t('Facility.Action.RenewFacility')}
                  variant='text'
                  color='primary'
                >
                  <Cached />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Popover>
        {!openFacilityActions ? (
          <IconButton
            onClick={handleOpenFacilityActions}
            id={'facilityHeader_actionList_showBtn'}
            tooltip={t('Tooltips.ShowHeaderActions')}
            variant='text'
            color='primary'
          >
            <List />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleCloseFacilityActions}
            id={'facilityHeader_actionList_hideBtn'}
            tooltip={t('Tooltips.HideHeaderActions')}
            variant='text'
            color='primary'
          >
            <Remove />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

FacilityHeader.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  onChangeShowReportsModal: PropTypes.func.isRequired,
  loadFacilityReports: PropTypes.func.isRequired,
  openFacilityActions: PropTypes.bool.isRequired,
  handleCloseFacilityActions: PropTypes.func.isRequired,
  handleOpenFacilityActions: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  anchorEl: PropTypes.object
};

export default withParams(FacilityHeader);
